import { Button, Card, Form, Select, Skeleton, Switch } from "antd";
import {
    DownloadOutlined,
    BarChartOutlined,
    LineChartOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { useContext, useEffect, useState } from "react";
import { useQuery } from "react-query";
import { SettingContext } from "../../routes";
import HTTP from "../../services/httpService";
import { downloadCSV, formatDate, hotelTypeOptions } from "../../services/util";
import { DashboardGrids } from "../Home/Home";
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Cell,
} from "recharts";

const { Option } = Select;

const xAxis = [
    { key: "monthly", title: "/Month" },
    { key: "yearly", title: "/Year" },
    { key: "days", title: "/Day" },
];

const allGrids = [...DashboardGrids];
const yAxis = allGrids.filter(
    (e) => e.key !== "hotel_total" && e.key !== "upcoming_total",
);

// ── Palette ───────────────────────────────────────────
const COLORS = [
    "var(--color-primary)",
    "var(--color-accent)",
    "var(--color-warning)",
    "var(--color-primary-hover)",
    "var(--color-success)",
    "var(--color-error)",
];

// ── Axis / grid shared styles ─────────────────────────
const axisStyle = {
    tick: { fill: "var(--color-text-muted)", fontSize: 11 },
    axisLine: { stroke: "var(--color-border)" },
    tickLine: false,
};

// ── Custom Tooltip ────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div
            style={{
                background: "var(--color-bg-control)",
                border: "1px solid var(--color-border)",
                borderRadius: 8,
                padding: "10px 14px",
                fontSize: 13,
                boxShadow: "var(--shadow-card)",
            }}
        >
            <p
                style={{
                    margin: 0,
                    fontWeight: 600,
                    marginBottom: 6,
                    color: "var(--color-text-secondary)",
                    fontSize: 11,
                }}
            >
                {label}
            </p>
            {payload.map((entry, i) => (
                <p
                    key={i}
                    style={{
                        margin: "2px 0",
                        color: entry.color || entry.fill,
                    }}
                >
                    {entry.name}:{" "}
                    <strong style={{ color: "var(--color-text)" }}>
                        {entry.value}
                    </strong>
                </p>
            ))}
        </div>
    );
};

// ── Data transform ────────────────────────────────────
// Google Charts 2D array → Recharts object array
function transformData(rawData) {
    if (!rawData || rawData.length < 2) return { data: [], keys: [], xKey: "" };
    const [headers, ...rows] = rawData;
    const [xKey, ...seriesKeys] = headers;
    const data = rows.map((row) => {
        const obj = { [xKey]: String(row[0]) };
        seriesKeys.forEach((k, i) => {
            obj[k] = Number(row[i + 1]) || 0;
        });
        return obj;
    });
    return { data, keys: seriesKeys, xKey };
}

// ── Filter label style ────────────────────────────────
const LabelStyle = ({ children }) => (
    <span
        style={{
            color: "var(--color-text-muted)",
            fontSize: 12,
            fontWeight: 500,
        }}
    >
        {children}
    </span>
);

// ── Toggle row ────────────────────────────────────────
function ToggleRow({ label, children }) {
    return (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span
                style={{
                    color: "var(--color-text-muted)",
                    fontSize: 12,
                    fontWeight: 500,
                    minWidth: 110,
                }}
            >
                {label}
            </span>
            {children}
        </div>
    );
}

// ── Main component ────────────────────────────────────
function HotelAnalytics() {
    const staff_id = localStorage.getItem("x-dashboard-staffid");
    const [form] = Form.useForm();
    const { selectedDates } = useContext(SettingContext);
    const [start_date, end_date] = selectedDates;

    const [chartData, setChartData] = useState([]);
    const [chartType, setChartType] = useState("line"); // "line" | "bar"
    const [yAxisOptions, setYAxisOptions] = useState(
        yAxis.reduce((prev, cur) => {
            if (
                ![
                    "android_user_count",
                    "guest_user_count",
                    "ios_user_count",
                    "staff_user_count",
                ].includes(cur.key)
            ) {
                prev.push(cur);
            }
            return prev;
        }, []),
    );
    const [xAxisOptions, setXAxisOptions] = useState(xAxis);
    const [yAxisSelection, setYAxisSelection] = useState(yAxisOptions[0].key);
    const [xAxisSelection, setXAxisSelection] = useState(xAxisOptions[0].key);
    const [hotelType, setHotelType] = useState(hotelTypeOptions[0].key);
    const [hotelOptions, setHotelOptions] = useState([]);

    const [formData, setFormData] = useState({
        staff_id,
        all_selected: false,
        start_date: formatDate(start_date),
        end_date: formatDate(end_date),
        y_param: yAxisSelection,
        x_param: xAxisSelection,
        hotel_type: "all",
    });

    const { status: hotelStatus, data: hotelData } = useQuery(
        "analytics-hotels",
        async () =>
            await HTTP("/dashboard/getHotels", { staff_id, hotel_type: "all" }),
    );
    const { status: kioskHotelStatus, data: kioskHotelData } = useQuery(
        "kiosk-hotels",
        async () =>
            await HTTP("/dashboard/getKioskHotels", {
                staff_id,
                limit: 1000,
                page: 0,
            }),
    );
    const { status: OperationalHotelStatus, data: OperationalHotelData } =
        useQuery(
            "operational-hotels",
            async () =>
                await HTTP("/dashboard/getOperationalHotels", {
                    staff_id,
                    limit: 1000,
                    page: 0,
                }),
        );
    const {
        status: chartStatus,
        data: chartRes,
        refetch: getChartData,
    } = useQuery(
        "analyticsChart",
        async () => await HTTP("/analytics/getAnalyticsData", { ...formData }),
        { enabled: false },
    );

    useEffect(() => {
        if (hotelStatus === "success") {
            setHotelOptions(hotelData.data);
            const firstId = hotelData.data[0].hotel_id;
            setFormData((p) => ({ ...p, hotel_ids: [firstId] }));
            form.setFieldsValue({ hotel_id: [firstId] });
        }
    }, [hotelStatus, hotelData]);

    useEffect(() => {
        const [s, e] = selectedDates;
        const updated = {
            ...formData,
            start_date: formatDate(s),
            end_date: formatDate(e),
        };
        if (updated.chart_for === "comparison") {
            const diff = moment(e).diff(moment(s), "days");
            updated.previous_start_date = formatDate(
                moment(s).subtract(diff === 0 ? 1 : diff, "days"),
            );
            updated.previous_end_date = formatDate(
                moment(s).subtract(1, "days"),
            );
        }
        setFormData(updated);
    }, [selectedDates]);

    useEffect(() => {
        getChartData();
    }, [formData]);

    useEffect(() => {
        if (chartStatus === "success") {
            const { data, success } = chartRes;
            if (success) {
                setChartData(
                    data.map((e, i) => {
                        if (i > 0) e[1] = parseFloat(e[1]);
                        return e;
                    }),
                );
            }
        }
    }, [chartStatus, chartRes]);

    function handleYAxisChange(event) {
        setYAxisSelection(event);
        setFormData((p) => ({ ...p, y_param: event }));
        const opts = xAxis.reduce((prev, cur) => {
            prev.push(cur);
            if (
                [
                    "android_user_count",
                    "guest_user_count",
                    "ios_user_count",
                    "staff_user_count",
                ].includes(event)
            ) {
                if (cur.key === "hotel_name") prev.pop();
            }
            return prev;
        }, []);
        setXAxisOptions(opts);
    }

    function handleXAxisChange(event) {
        setXAxisSelection(event);
        setFormData((p) => ({ ...p, x_param: event }));
        setYAxisOptions(updateYAxisOptions(event, formData.hotel_type));
    }

    function updateYAxisOptions(x_param, hotel_type) {
        let opts = [...yAxis];
        opts = opts.filter((cur) => {
            if (
                [
                    "android_user_count",
                    "guest_user_count",
                    "ios_user_count",
                    "staff_user_count",
                ].includes(cur.key)
            ) {
                return x_param !== "hotel_name";
            }
            return true;
        });
        opts = opts.filter((cur) => {
            if (
                [
                    "dinning_order_count",
                    "hk_order_count",
                    "hk_ticket_count",
                    "mnt_ticket_count",
                ].includes(cur.key)
            ) {
                return hotel_type !== "kiosk";
            }
            return hotel_type !== "operation";
        });
        return opts;
    }

    function handleCompare(enabled) {
        if (enabled) {
            const { start_date, end_date } = formData;
            const diff = moment(end_date).diff(moment(start_date), "days");
            setFormData({
                ...formData,
                chart_for: "comparison",
                previous_start_date: formatDate(
                    moment(start_date).subtract(diff === 0 ? 1 : diff, "days"),
                ),
                previous_end_date: formatDate(
                    moment(start_date).subtract(1, "days"),
                ),
            });
        } else {
            const updated = { ...formData };
            delete updated.chart_for;
            delete updated.previous_start_date;
            delete updated.previous_end_date;
            setFormData(updated);
        }
    }

    function handleHotelCompare(enabled) {
        if (enabled) {
            setFormData((p) => ({ ...p, chart_for: "hotel_comparison" }));
        } else {
            const updated = { ...formData };
            delete updated.chart_for;
            delete updated.previous_start_date;
            delete updated.previous_end_date;
            setFormData(updated);
        }
    }

    function updateSelectedYAxis(newOpts) {
        const found = newOpts.find((e) => e.key === yAxisSelection);
        if (!found) {
            setYAxisSelection(newOpts[0].key);
            return newOpts[0].key;
        }
        return null;
    }

    function handleHotelTypeSelection(e) {
        setHotelType(e);
        let updated = { ...formData, hotel_type: e };
        if (e === "all" && hotelStatus === "success") {
            setHotelOptions(hotelData.data);
            updated.hotel_ids = [hotelData.data[0].hotel_id];
            form.setFieldsValue({ hotel_id: [hotelData.data[0].hotel_id] });
        } else if (e === "kiosk" && kioskHotelStatus === "success") {
            setHotelOptions(kioskHotelData.data);
            updated.hotel_ids = [kioskHotelData.data[0].id];
            form.setFieldsValue({ hotel_id: [kioskHotelData.data[0].id] });
        } else if (OperationalHotelStatus === "success") {
            setHotelOptions(OperationalHotelData.data);
            updated.hotel_ids = [OperationalHotelData.data[0].id];
            form.setFieldsValue({
                hotel_id: [OperationalHotelData.data[0].id],
            });
        }
        const updatedOpts = updateYAxisOptions(formData.x_param, e);
        setYAxisOptions(updatedOpts);
        const newSel = updateSelectedYAxis(updatedOpts);
        if (newSel) updated.y_param = newSel;
        setFormData(updated);
    }

    function handleHotelSelection(e) {
        setFormData((p) => ({
            ...p,
            hotel_ids: e,
            all_selected: e.length === 0,
        }));
    }

    async function Download() {
        const res = await HTTP("/analytics/getAnalyticsDataReport", {
            ...formData,
        });
        downloadCSV(res, "Analytics");
    }

    const { data: parsed, keys, xKey } = transformData(chartData);
    const isReady = chartStatus === "success" && parsed.length > 0;

    const ChartElement =
        chartType === "bar" ? (
            <ResponsiveContainer width="100%" height={440}>
                <BarChart
                    data={parsed}
                    margin={{ top: 10, right: 20, left: 0, bottom: 5 }}
                    barCategoryGap="30%"
                >
                    <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="var(--color-border-light)"
                        vertical={false}
                    />
                    <XAxis dataKey={xKey} {...axisStyle} />
                    <YAxis {...axisStyle} allowDecimals={false} />
                    <Tooltip
                        content={<CustomTooltip />}
                        cursor={{ fill: "var(--color-primary-light)" }}
                    />
                    <Legend
                        wrapperStyle={{ fontSize: 12, paddingTop: 10 }}
                        formatter={(v) => (
                            <span style={{ color: "var(--color-text-secondary)" }}>
                                {v}
                            </span>
                        )}
                    />
                    {keys.map((key, i) => (
                        <Bar
                            key={key}
                            dataKey={key}
                            fill={COLORS[i % COLORS.length]}
                            radius={[4, 4, 0, 0]}
                            maxBarSize={56}
                        />
                    ))}
                </BarChart>
            </ResponsiveContainer>
        ) : (
            <ResponsiveContainer width="100%" height={440}>
                <LineChart
                    data={parsed}
                    margin={{ top: 10, right: 20, left: 0, bottom: 5 }}
                >
                    <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="var(--color-border-light)"
                        vertical={false}
                    />
                    <XAxis dataKey={xKey} {...axisStyle} />
                    <YAxis {...axisStyle} allowDecimals={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                        wrapperStyle={{ fontSize: 12, paddingTop: 10 }}
                        formatter={(v) => (
                            <span style={{ color: "var(--color-text-secondary)" }}>
                                {v}
                            </span>
                        )}
                    />
                    {keys.map((key, i) => (
                        <Line
                            key={key}
                            type="monotone"
                            dataKey={key}
                            stroke={COLORS[i % COLORS.length]}
                            strokeWidth={2}
                            dot={false}
                            activeDot={{ r: 5 }}
                        />
                    ))}
                </LineChart>
            </ResponsiveContainer>
        );

    return (
        <div
            style={{
                background: "var(--color-bg-card)",
                border: "1px solid var(--color-border)",
                borderRadius: 12,
                padding: "20px 24px",
            }}
        >
            {/* ── Header ── */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 20,
                }}
            >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div
                        style={{
                            width: 3,
                            height: 18,
                            borderRadius: 2,
                            background: "var(--gradient-primary)",
                        }}
                    />
                    <span
                        style={{
                            color: "var(--color-text)",
                            fontWeight: 600,
                            fontSize: 15,
                        }}
                    >
                        Hotel Analytics
                    </span>
                </div>
                <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={Download}
                    size="small"
                    style={{ borderRadius: 7 }}
                >
                    Export CSV
                </Button>
            </div>

            {/* ── Filter panel ── */}
            <div
                style={{
                    background: "var(--color-bg-control)",
                    border: "1px solid var(--color-border)",
                    borderRadius: 10,
                    padding: "16px 20px",
                    marginBottom: 20,
                }}
            >
                <Form form={form} size="small">
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "4px 32px",
                        }}
                    >
                        {/* Hotel Type */}
                        <Form.Item
                            label={<LabelStyle>Hotel Type</LabelStyle>}
                            style={{ marginBottom: 10 }}
                        >
                            <Select
                                onChange={handleHotelTypeSelection}
                                defaultValue={hotelType}
                            >
                                {hotelTypeOptions.map((e) => (
                                    <Option key={e.key}>{e.title}</Option>
                                ))}
                            </Select>
                        </Form.Item>

                        {/* Hotels */}
                        <Form.Item
                            label={<LabelStyle>Hotels</LabelStyle>}
                            name="hotel_id"
                            style={{ marginBottom: 10 }}
                        >
                            <Select
                                showSearch
                                mode="multiple"
                                placeholder="Select hotels"
                                onChange={handleHotelSelection}
                                loading={!hotelOptions.length}
                                filterOption={(input, option) =>
                                    option.children
                                        .toLowerCase()
                                        .includes(input.toLowerCase())
                                }
                            >
                                {hotelOptions.map((h) => (
                                    <Option
                                        key={h.hotel_id || h.id}
                                        value={h.hotel_id || h.id}
                                    >
                                        {`${h.hotel_id || h.id} - ${
                                            h.hotel_name || h.name
                                        } - ${h.city}`}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>

                        {/* X-Axis */}
                        <Form.Item
                            label={<LabelStyle>X-Axis</LabelStyle>}
                            style={{ marginBottom: 10 }}
                        >
                            <Select
                                onChange={handleXAxisChange}
                                value={xAxisSelection}
                                defaultValue={xAxisSelection}
                            >
                                {xAxisOptions.map((e) => (
                                    <Option key={e.key}>{e.title}</Option>
                                ))}
                            </Select>
                        </Form.Item>

                        {/* Y-Axis */}
                        <Form.Item
                            label={<LabelStyle>Y-Axis</LabelStyle>}
                            style={{ marginBottom: 10 }}
                        >
                            <Select
                                showSearch
                                onChange={handleYAxisChange}
                                value={yAxisSelection}
                                defaultValue={yAxisSelection}
                                filterOption={(input, option) =>
                                    option.children
                                        .toLowerCase()
                                        .includes(input.toLowerCase())
                                }
                            >
                                {yAxisOptions.map((e) => (
                                    <Option key={e.key}>{e.title}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </div>

                    {/* Toggle switches row */}
                    <div
                        style={{
                            display: "flex",
                            gap: 24,
                            flexWrap: "wrap",
                            paddingTop: 12,
                            borderTop: "1px solid var(--color-border)",
                            marginTop: 4,
                        }}
                    >
                        <ToggleRow label="Compare Period">
                            <Switch
                                size="small"
                                disabled={
                                    formData?.chart_for === "hotel_comparison"
                                }
                                onChange={handleCompare}
                            />
                        </ToggleRow>
                        <ToggleRow label="Compare Hotels">
                            <Switch
                                size="small"
                                disabled={
                                    formData?.chart_for === "comparison" ||
                                    (formData?.hotel_ids?.length ?? 0) <= 1
                                }
                                onChange={handleHotelCompare}
                            />
                        </ToggleRow>
                        <ToggleRow label="Bar Chart">
                            <Switch
                                size="small"
                                onChange={(enabled) =>
                                    setChartType(enabled ? "bar" : "line")
                                }
                                checkedChildren={<BarChartOutlined />}
                                unCheckedChildren={<LineChartOutlined />}
                            />
                        </ToggleRow>
                    </div>
                </Form>
            </div>

            {/* ── Chart ── */}
            {isReady ? (
                ChartElement
            ) : (
                <Skeleton
                    active
                    title={false}
                    paragraph={{ rows: 10, width: "100%" }}
                />
            )}
        </div>
    );
}

export { HotelAnalytics };
